import React from 'react'
import { OmInput, OmSelect } from '@owma/kit'

import i18n from 'i18n'
import JSZip from 'jszip'
import classNames from 'classnames'
import { store as reduxStore } from 'index'
import { isEqual } from 'lodash'
import dataManager from 'pages/main/data/DataManager'
import { getMessageId } from 'pages/main/data/JMAPMailbox'
import { TYPE } from 'pages/main/data/defConstants'
import { isSearchEmailMode } from 'pages/main/data/searchDisplayMode'
import { getEnvParam, useAiSummary } from 'env'
import conf from 'conf'
import { getImageInfo } from 'components/FTIcon/getImageInfo'
import { generateFilename, FileNameMgr } from 'utils'
import { openPrintWindow } from 'utils/ui/printEmail'
import type { PrintPayload } from 'emailThreadViewer/EmailView/printPayload'
import type {
  ViewerWindow,
  NativeToWebApi,
  NineNativeApi,
} from '@owma/common-web/components/viewer'
import { setDraftBodyStyleSkeleton } from '../draftBodyStyleCache'
import {
  BODY_DOC_FROM_VIEWER,
  clearParsedBodyDocuments,
  setParsedBodyDocument,
} from '../parsedBodyDocCache'
import logoNoIconSVG from 'assets/images/logo_no_icon.svg'
import calendar2SVG from 'assets/images/icon-calendar2.svg'

import { showResult, popupDoc, notImplemented, confirmBase, errorBase } from 'utils/ui'
// import { secureSendOptions } from 'utils/ui/SecureSend'
import secureSendApiHelper from 'services/secure-send'
import type { SecureMailStatusResponse } from 'services/secure-send'
import readReceiptApiHelper from 'services/read-receipt'
import type { SendStatusResponse } from 'services/read-receipt'
import readReceiptStatusCacheDB from 'libs/DB/readReceiptStatusCacheDB'
import secureMailStatusCacheDB from 'libs/DB/secureMailStatusCacheDB'

import { READ_RECEIPT_RETENTION_MS, isMailSentByMe, maybeReportRead } from './readReceiptHelpers'
import { buildReadReceiptAllowOrigins, isReadReceiptOrigin } from './readReceiptAllowOrigin'
import { openReadReceiptDetailPopup } from './ReadReceiptDetailPopup'
import { notifyReadReceipt } from './readReceiptListenerStore'

import { loadThreadInfo, loadAiExtractionSample } from './emailHelper'
import { meetingResponseByUid } from 'pages/calendar/modules/CalendarService'
import { modalContactDetail } from 'pages/contacts/components/ContactDetail'
import attachmentHelper from 'pages/main/components/Attachment/helper'
import { getImagePath, sweepObjectURLMap } from 'libs/CACHE/jmapDownloadCache'

import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import exclamationCircleRed22 from 'assets/images/exclamation-circle-red-22.svg'

import { ThreadView } from './index'

import { callViewerCallback } from 'emailThreadViewer/helper/viewerCallback'

import styles from './style.module.scss'

const threadInfo = await loadThreadInfo()
const aiExtractionSample = await loadAiExtractionSample()

// OMD-191: 외부 이미지 차단 예외로 허용할 수신확인 서버 origin 집합. 현재 환경의
// conf.SERVER.readReceiptApiURL origin + 운영/개발 fallback origin 을 모듈 로드 시 1회 계산한다.
const READ_RECEIPT_ALLOW_ORIGINS = buildReadReceiptAllowOrigins(conf.SERVER.readReceiptApiURL)

interface ThreadViewInfo {
  viewerWindow?: ViewerWindow
  initNative?: () => void
  // OMD-194: 인쇄 위임으로 열린 창. viewer teardown 시 정리(close)한다.
  printWindow?: Window | null
}

interface CommentDialogOptions {
  title: string
  type: number
  placeholder: string
  onOk?: (type: number, text: string) => void
  afterClose?: () => void
}

const threadViewInfo: ThreadViewInfo = {}

// attachment.download() 결과(ret.path / ret.downloadPath)는 jmapDownloadCache가
// 공유 객체로 반환할 수 있으므로, 이 레이어에서 직접 revokeObjectURL을 호출하면
// 이후 캐시 hit 시 재사용되는 URL이 무효화되어 inline 이미지가 깨지거나 재다운로드가 실패한다.
// 따라서 download 결과의 blob URL 수명은 jmapDownloadCache(beforeRemoveSaveData 훅)에서 관리하며,
// 이 파일에서는 캐시 밖에서 생성한 blob URL(downloadAll의 zip 등)만 직접 해제한다.
function safeRevokeObjectURL(url: string | null | undefined): void {
  if (url == null || url.length === 0) return
  try {
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.warn('revokeObjectURL failed:', url, e)
  }
}

// IFrameViewer unmount 시 iframe.src='about:blank'로 네비게이트되면, 동일 WindowProxy인
// viewerWindow가 새(about:blank) 문서를 가리켜 viewerWindow.webObj가 undefined가 된다.
// 네트워크/타이머 등 늦게 resolve되는 비동기 콜백이 viewerWindow.webObj.xxx()를 호출하면
// TypeError가 나므로, 비동기 경로에서는 이 헬퍼로 안전하게 접근한다. (동기 NineNative
// 핸들러는 viewer가 살아있을 때만 호출되므로 가드 불필요. InnerViewer는 host window라
// about:blank 네비게이트가 없어 항상 non-null — 이 가드는 무해.)
function getViewerWebObj(viewerWindow: ViewerWindow | null | undefined): NativeToWebApi | null {
  try {
    return viewerWindow != null && viewerWindow.webObj != null ? viewerWindow.webObj : null
  } catch {
    return null
  }
}

// IFrameViewer unmount 시 호출 — host 전역(window.initNative)과 모듈 변수가
// detached iframe window(뷰어 문서 전체)를 붙잡아 메모리 누수가 되지 않도록 해제한다.
// 다중 ThreadView가 빠르게 mount/unmount될 때(새 인스턴스가 window.initNative를 덮어쓴 뒤
// 옛 인스턴스가 unmount되는 경쟁), 옛 인스턴스가 새 인스턴스의 등록을 지우지 않도록
// "이 인스턴스가 등록한 함수(ownInitNative)와 동일할 때만" 해제한다.
export function disposeViewerApi(ownInitNative?: () => void): void {
  // window.initNative: 우리가 등록한 바로 그 함수일 때만 삭제 (새 인스턴스 것 보존).
  if (ownInitNative != null && window.initNative === ownInitNative) {
    delete window.initNative
  }
  // threadViewInfo: 아직 우리 소유일 때만(또는 토큰 미전달 시) 정리.
  if (ownInitNative == null || threadViewInfo.initNative === ownInitNative) {
    threadViewInfo.initNative = undefined
    threadViewInfo.viewerWindow = undefined
    // OMD-194: 열린 인쇄 창을 닫는다(기존 EmailView.componentWillUnmount 동작 이식).
    if (threadViewInfo.printWindow != null && !threadViewInfo.printWindow.closed) {
      threadViewInfo.printWindow.close()
    }
    threadViewInfo.printWindow = null
    // draft-body-doc-from-viewer: 뷰어 완전 이탈(메일 모듈 이탈/언마운트) 시 선파싱 Document
    // 를 폐기한다 — thread 전환 clear(applyThread)가 못 덮는 마지막 thread 잔존분 해제.
    // 소유권 가드 안이라, 새 인스턴스가 이미 등록을 덮은 경쟁에서는 새 캐시를 지우지 않는다.
    clearParsedBodyDocuments()
  }
}

export function initViewerApi(viewerWindow: ViewerWindow, reactObj: ThreadView): void {
  console.log('initViewerApi')

  window.initNative = () => {
    // host -> viewer callback bridge. inner mode (`viewerWindow === window`) 는 helper
    // 내부 localStore 의 singleUse callback 만 의미가 있고, iframe mode 는 viewer
    // contentWindow 의 nested property chain 으로 호출. 양 모드 분기는
    // [helper/viewerCallback.ts](../../../../../../packages/email-thread-viewer/src/helper/viewerCallback.ts).
    const callCBStr = (CBStr: string, params: any[]): void => {
      callViewerCallback(viewerWindow, CBStr, params)
    }

    threadViewInfo.viewerWindow = viewerWindow

    if (reactObj.applyThreadFunc == null) {
      reactObj.isFirstSet = true

      reactObj.applyThreadFunc = () => {
        const { emails: initEmails, curMailbox, curEmail } = reactObj.props

        if (viewerWindow == null || viewerWindow.webObj == null) {
          // 실행 준비가 안된 상태임.
          return
        }

        // console.log(' --- applyThread : ', initEmails, curMailbox, curEmail)

        // if (curEmail != null) {
        //   console.log(' --- applyThread 1 : ', curEmail.data.id, initEmails.length)
        // }

        const emailIds = (initEmails || []).map((item: any) => item.data.id)
        const curEmailId = curEmail != null ? curEmail.data.id : null
        const curThreadId = curEmail != null ? curEmail.data.threadId : null

        // let addList = [];
        // let deleteList = [];

        let threadChanged = true

        if (reactObj.prevThreadInfo != null) {
          const {
            emailIds: prevEmailIds,
            curEmailId: prevCurEmailId,
            curThreadId: prevCurThreadId,
          } = reactObj.prevThreadInfo
          // 변경된 사항이 없으면 바로 리턴.
          // deleteList = [...prevEmailIds];
          // addList = [...emailIds];

          // for(var i = deleteList.length - 1; i >= 0; i -= 1) {
          //   const deleteEmailId = deleteList[i];
          //   for(var j = addList.length - 1; j >= 0; j -= 1) {
          //     const addEmailId = addList[j];
          //     if (deleteEmailId === addEmailId) {
          //       deleteList.splice(i, 1);
          //       addList.splice(j, 1);
          //       break;
          //     }
          //   }
          // }

          threadChanged = curThreadId !== prevCurThreadId

          if (
            isEqual(emailIds, prevEmailIds) === true &&
            curEmailId === prevCurEmailId &&
            threadChanged === false
          ) {
            return
          }
        }

        console.log(' --- applyThread : ', initEmails, curMailbox, curEmail)

        // draft-body-doc-from-viewer: thread 가 바뀌면 이전 thread 의 draft 선파싱 Document 를
        // 전부 폐기해 메모리 상주를 현재 thread 로 한정한다(사용자 결정 스코프). 새 thread 의
        // draft 는 펼침 bake 시 다시 보고된다.
        if (threadChanged === true) {
          clearParsedBodyDocuments()
        }

        reactObj.prevThreadInfo = {
          emailIds,
          curEmailId,
          curThreadId,
        }

        let emails = initEmails

        // OFM-222: 검색(OpenSearch) 모드에서는 목록과 동일하게 개별 이메일 한 건만 표시한다.
        // curMailbox(=this.mailbox)는 검색 진입 시 원본 폴더(THREAD)를 유지하므로, 검색 여부를
        // searchMailbox 기준으로 질의해 검색 이메일 모드면 스레드 확장을 건너뛴다.
        const isThreadMode =
          isSearchEmailMode(dataManager.searchMailbox) !== true &&
          curMailbox != null &&
          curMailbox.getListType() === TYPE.THREAD

        if (isThreadMode === true && emails != null && emails.length > 0) {
          const thread = curMailbox.getThreadById(emails[0].data.threadId)
          if (thread != null) {
            const threadEmailfilter = curMailbox.getThreadEmailfilter()
            const sortFunc = curMailbox.getSortFunc()
            emails = thread.data.emailIds
              .map((emailId: string) => {
                const email = dataManager.getEmailById(emailId)
                return email
              })
              .filter(threadEmailfilter)
              .sort(sortFunc)
          }
        }

        // createEmailInfo 가 메일함 이름 계산 여부를 판단하도록 현재 thread 보기 여부를 전달.
        // (단일 메일 보기면 메일함 이름을 뷰어에 넘기지 않는다.)
        reactObj.isThreadMode = isThreadMode === true

        // blob URL의 수명은 jmapDownloadCache가 단일 책임으로 관리한다.
        reactObj.emailInfoMap = {}

        // thread 전환 = 이전 thread의 inline 이미지/첨부 path가 더 이상 DOM에 없을
        // 가능성이 큰 시점. main document + iframe outerHTML로 사용 여부를 검사해 미사용 url을 회수.
        // 비용 가드(D20)가 sweep 내부에서 작동하므로 호출자는 단순히 trigger만.
        sweepObjectURLMap()

        let emailList: any[] = []

        const emailInfoList: any[] = []
        if (emails != null) {
          emailList = emails
          emails.forEach((item: any) => {
            // emailIFace - 이메일 형식을 하나로 통일하기 위한 인터페이스 구현 필요.
            // const emailItem = emailIFace(item); // ...
            const emailInfo = reactObj.createEmailInfo(item)
            emailInfoList.push(emailInfo.dataForJSON)
          })
        }

        if (emailInfoList.length === 0) {
          viewerWindow.webObj.clearThread()
          reactObj.closeLoadingAni()
          return
        }

        const threadData: any = {
          emailList: emailInfoList,
        }

        if (threadInfo != null) {
          threadData.info = threadInfo
        }

        const threadDataStr = JSON.stringify(threadData)
        const firstEmailId = emailList.length > 0 ? emailList[0].data.id : null

        let aiExtractionEmailId = firstEmailId

        const emailInfo = reactObj.emailInfoMap[firstEmailId]
        if (emailInfo != null) {
          const { api } = emailInfo
          if (api.hasAiExtraction() !== true) {
            aiExtractionEmailId = null
          }
        }

        // if (reactObj.isFirstSet === true) {
        //   reactObj.isFirstSet = false
        // } else if (threadChanged === true) {
        //   reactObj.showLoadingAni(500)
        // }

        viewerWindow.webObj.setThread(threadDataStr, curEmailId, aiExtractionEmailId, true)
        // viewerWindow.webObj.setThread(threadDataStr, null, null)
        // setTimeout(() => {
        //   viewerWindow.webObj.select(emails[0].data.id);
        // }, 100);

        if (curMailbox != null) {
          // dataManager 의 default export type 이 searchMailbox 멤버를 명시 안 함 — inline
          // narrowing 으로 사용처 (isSearchMode / searchText) 의 최소 shape 만 좁힘.
          const searchMailbox = (
            dataManager as unknown as {
              searchMailbox: { isSearchMode: () => boolean; searchText: string | null }
            }
          ).searchMailbox

          if (searchMailbox.isSearchMode() === true) {
            if (searchMailbox.searchText != null) {
              setTimeout(() => {
                // unmount 후 발화할 수 있는 타이머라 webObj 가드.
                const webObj = getViewerWebObj(viewerWindow)
                if (webObj != null) {
                  webObj.setFindText(searchMailbox.searchText)
                }
              }, 200)
            }
          }
        }
      }
    }

    viewerWindow.NineNative = {
      getNativeType: (): string => {
        return 'web'
      },
      // OMD-191: 외부 이미지 차단 예외 판별. 수신확인 서버 origin 과 완전히 일치하는 픽셀만
      // 예외(false, 차단 안 함)로 처리하고, 그 외 외부 이미지는 true(차단)를 반환한다.
      // ⚠️ 반드시 동기(sync) boolean 반환 — async/Promise 금지. viewer 의 img hot-path 에서
      // 동기 값이 필요하며, non-boolean 반환은 뷰어 브릿지에서 무시되어 차단으로 폴백된다.
      isExternalImageSrc: (src: string): boolean =>
        !isReadReceiptOrigin(src, READ_RECEIPT_ALLOW_ORIGINS),
      // OMD-194: 인쇄 오케스트레이션(host). 뷰어(NativeToWeb.requestPrintPayload)에서 payload 를
      // 되가져와(fold 상태면 뷰어가 펼친 뒤 async 콜백) owma 자원(로고·캘린더 아이콘)을 더해
      // 인쇄 문서를 조립·인쇄한다. 트리거 A(뷰어 more 메뉴 → WebToNative.print)와 B(목록 액션 →
      // IFrameViewer/InnerViewer.printEmail)가 모두 이 진입점으로 수렴. 실제 구현은 utils/ui/printEmail.ts.
      print: (emailId: string): void => {
        const webObj = getViewerWebObj(viewerWindow)
        if (webObj == null || typeof webObj.requestPrintPayload !== 'function') {
          console.warn('NineNative.print: requestPrintPayload 미가용', emailId)
          return
        }
        webObj.requestPrintPayload(emailId, (payload: PrintPayload | null) => {
          if (payload == null) {
            console.warn('NineNative.print: payload 없음', emailId)
            return
          }
          // 이전 인쇄 창이 남아 있으면 정리(최대 1개만 유지).
          if (threadViewInfo.printWindow != null && !threadViewInfo.printWindow.closed) {
            threadViewInfo.printWindow.close()
          }
          threadViewInfo.printWindow = openPrintWindow({
            ...payload,
            logoSrc: logoNoIconSVG,
            calendarIconSrc: calendar2SVG,
          })
        })
      },
      onReady: function (this: any): string {
        console.log(' --- onReady')

        const reduxState = reduxStore.getState()
        const { settings } = reduxState
        const {
          general: { dateFormat, timeFormat },
          ai,
        } = settings
        // locale 은 local-only — localSettings.general.locale 에서 읽는다(서버 sync 대상 아님).
        const locale = settings.localSettings?.general?.locale

        // 본문을 shadow root 에 격리해 document·viewer stylesheet 가 본문에 매칭되지 않게 한다
        // (대형 본문 첫 style recalc 절감). 미지원 환경은 mountBodyRoot 가 light-dom 으로 폴백한다.
        // 벤치마크/롤백 escape hatch: window.__viewerBodyIsolation 로 런타임 전환(기본 shadow-dom).
        const isolationOverride = (window as unknown as { __viewerBodyIsolation?: string })
          .__viewerBodyIsolation
        const bodyIsolation =
          isolationOverride === 'light-dom' || isolationOverride === 'shadow-dom'
            ? isolationOverride
            : 'shadow-dom'

        const settingsObj: any = {
          useAiSummary: false,
          // dateFormat: 'YYYY/M/d',
          // timeFormat: 'h:mm A',
          dateFormat,
          timeFormat,
          defaultBgColor: '#f9fafb',
          bodyIsolation,
        }

        if (useAiSummary() === true) {
          settingsObj.useAiSummary = ai.summary
        }

        const settingsStr = JSON.stringify(settingsObj)

        // 언어 초기값 설정. locale 반영(SET_LOCALE dispatch)은 host 책임 — 뷰어의
        // NativeToWeb.setLanguage 는 리렌더만 수행한다 (extract-common-web: 뷰어의
        // 'index'(redux store) 의존 제거, dispatch 를 기존 뷰어 구현에서 이 지점으로 이동).
        reduxStore.dispatch({
          type: 'settings/SET_LOCALE',
          payload: {
            locale,
          },
        })
        viewerWindow.webObj.setLanguage(locale)
        viewerWindow.webObj.setSettings(settingsStr, false)
        // viewerWindow.webObj.setUserEmail(user.email)

        const userEmail = getEnvParam('userEmail')
        viewerWindow.webObj.setUserEmail(userEmail)

        reactObj.applyThread!()

        reactObj.applyCurEmail()
        reactObj.onReady()
        return ''
      },
      resizeHeight: (newHeight: number): void => {
        console.log(' --- resizeHeight : ', newHeight)
      },
      checkAllowedAndKnownSender: (emailId: string, senderEmail: string): Promise<string> => {
        const promise = new Promise<string>((resolve) => {
          const reduxState = reduxStore.getState()
          const { settings } = reduxState

          const { isUnknownAlwaysShowExternalResources, isKnownAlwaysShowExternalResources } =
            settings.email

          const sender = senderEmail

          let isBlocked = true
          let isAllowedSender: boolean | null = null

          const ret = dataManager.isTempAllowedEmailId(emailId)
          if (ret === true) {
            isBlocked = false

            const retProp = {
              isBlocked,
              isAllowedSender,
            }
            resolve(JSON.stringify(retProp))
          } else {
            // allowed email 과 known email은 다르게 처리해야 함.
            dataManager
              .checkAllowedSender(sender)
              .then((result: boolean) => {
                console.log(' --- checkAllowedSender : ', result)
                isAllowedSender = result

                if (result === true) {
                  isBlocked = false
                  const retProp = {
                    isBlocked,
                    isAllowedSender,
                  }
                  resolve(JSON.stringify(retProp))
                } else {
                  dataManager
                    .checkKnownSender(sender)
                    .then((isKnownSender: boolean) => {
                      if (isKnownSender === true) {
                        if (isKnownAlwaysShowExternalResources === 0) {
                          // always show
                          isBlocked = false
                        }
                      } else {
                        if (isUnknownAlwaysShowExternalResources === 0) {
                          // always show
                          isBlocked = false
                        }
                      }

                      const retProp = {
                        isBlocked,
                        isAllowedSender,
                      }
                      resolve(JSON.stringify(retProp))
                    })
                    .catch(() => {
                      const fallbackBlocked =
                        isKnownAlwaysShowExternalResources !== 0 &&
                        isUnknownAlwaysShowExternalResources !== 0
                      resolve(JSON.stringify({ isBlocked: fallbackBlocked, isAllowedSender }))
                    })
                }
              })
              .catch(() => {
                const fallbackBlocked =
                  isKnownAlwaysShowExternalResources !== 0 &&
                  isUnknownAlwaysShowExternalResources !== 0
                resolve(JSON.stringify({ isBlocked: fallbackBlocked, isAllowedSender: null }))
              })
          }
        })
        return promise
      },
      addAllowedSender: (sender: string): Promise<any> => {
        return dataManager.addAllowedSender(sender)
      },
      addTempAllowedEmailId: (emailId: string): void => {
        dataManager.addTempAllowedEmailId(emailId)
      },
      getFileIcon: (fileName: string): string => {
        const pos = fileName.lastIndexOf('.')
        const ext = fileName.substring(pos + 1)
        const info = getImageInfo(ext)

        const ret = {
          url: info.path,
          showExt: info.type === 'general',
        }
        const retStr = JSON.stringify(ret)
        return retStr
      },
      getAvatar: (email: string, name: string, returnFunc: string): null => {
        dataManager
          .getAddressObj(email, name)
          .getInfoForAvatar()
          .then((info: any) => {
            const { avatarUrl: avatar } = info
            const ret = {
              url: avatar,
            }
            const retStr = JSON.stringify(ret)
            callCBStr(returnFunc, [retStr])
          })
        return null
      },
      getBody: (emailId: string, returnFunc: string): null => {
        if (reactObj.loadingTimer != null) {
          reactObj.closeLoadingAni()
          clearTimeout(reactObj.loadingTimer)
          if (reactObj.loadingTimerRetureFunc != null) {
            callCBStr(reactObj.loadingTimerRetureFunc, ['null'])
            reactObj.loadingTimerRetureFunc = null
          }
        }

        if (reactObj.delayShowTimer != null) {
          clearTimeout(reactObj.delayShowTimer)
          reactObj.setNormalLoading(false)
        }

        reactObj.delayShowTimer = setTimeout(() => {
          reactObj.delayShowTimer = null
          reactObj.setNormalLoading(true)
        }, 300)

        reactObj.loadingTimerRetureFunc = returnFunc
        reactObj.loadingTimer = setTimeout(() => {
          reactObj.loadingTimer = null
          reactObj.loadingTimerRetureFunc = null
          const emailInfo = reactObj.emailInfoMap[emailId]
          if (emailInfo != null) {
            const { api } = emailInfo
            api
              .getBody()
              .then((jsonData: any) => {
                const jsonStr = JSON.stringify(jsonData)

                if (reactObj.emailInfoMap[emailId] != null) {
                  const emailInfo = reactObj.emailInfoMap[emailId]
                  const { inlineFiles } = emailInfo
                  if (inlineFiles != null) {
                    const inlineFilesMap: Record<string, any> = {}
                    emailInfo.inlineFilesMap = inlineFilesMap
                    inlineFiles.forEach((inlineFile: any) => {
                      inlineFile
                        .download()
                        .then((ret: any) => {
                          if (ret != null) {
                            // thread 교체 후 뒤늦게 도착한 결과는 무시.
                            if (reactObj.emailInfoMap[emailId] == null) return

                            const { cid, name, type, size } = inlineFile.file
                            // getImagePath: jmapDownloadCache.touch로 TTL을 갱신해 사용 중인
                            // path가 lazy expiration으로 evict되지 않도록 한다.
                            // miss race(cache는 이미 evict됐는데 ret.path는 살아있음)에는
                            // objectURLMap에 보강 add → 다음 sweep에서 정상 검사.
                            const url = getImagePath(ret, inlineFile.file) ?? ret.path
                            const inlineImages = [
                              {
                                cid,
                                name,
                                mime_type: type,
                                size,
                                url,
                              },
                            ]
                            inlineFilesMap[url] = inlineFile // attachment
                            const inlineImagesStr = JSON.stringify(inlineImages)

                            // 다운로드(네트워크)가 unmount 후 늦게 resolve될 수 있어 webObj 가드.
                            const webObj = getViewerWebObj(viewerWindow)
                            if (webObj != null) {
                              webObj.onSetInlineImages(emailId, inlineImagesStr)
                            }
                          }
                        })
                        .catch((err: any) => {
                          console.log(' --- getBody - inlineFile.download() error : ', err)
                        })
                    })
                  }
                }
                applySecureSendInfo(viewerWindow, emailId, reactObj)
                applyReadReceiptInfo(viewerWindow, emailId, reactObj)

                callCBStr(returnFunc, [jsonStr])

                reactObj.closeLoadingAni()

                clearTimeout(reactObj.delayShowTimer)
                reactObj.delayShowTimer = null
                reactObj.setNormalLoading(false)
              })
              .catch((err: any) => {
                console.error(' --- getBody error : ', err)

                callCBStr(returnFunc, ['null'])

                reactObj.closeLoadingAni()

                clearTimeout(reactObj.delayShowTimer)
                reactObj.delayShowTimer = null
                reactObj.setNormalLoading(false)
              })
          } else {
            callCBStr(returnFunc, ['null'])

            clearTimeout(reactObj.delayShowTimer)
            reactObj.delayShowTimer = null
            reactObj.setNormalLoading(false)
          }
        }, 50)

        return null
      },
      notifySelect: (emailId: string): void => {
        console.log(' --- notifySelect : ', emailId)
        const { runCommand } = reactObj.props
        const emailInfo = reactObj.emailInfoMap[emailId]
        const { email } = emailInfo
        runCommand('selectByViewer', [email])
      },
      setRead: (emailId: string, value: boolean, byUser: boolean): Promise<boolean> => {
        const { api, email } = reactObj.emailInfoMap[emailId]
        if (byUser === true) {
          const { runCommand } = reactObj.props
          const cmd = value === true ? 'read' : 'unread'
          runCommand(cmd, [email], true)
          return Promise.resolve(value)
        } else {
          return api.setRead(value, true)
        }
      },
      setFlagged: (emailId: string, value: boolean): Promise<any> => {
        const { api } = reactObj.emailInfoMap[emailId]
        return api.setFlagged(value)
      },
      delete: (emailId: string): void => {
        const { runCommand } = reactObj.props
        const emailInfo = reactObj.emailInfoMap[emailId]
        const { email } = emailInfo
        runCommand('delete', [email], true)
      },
      // draft-body-style-from-viewer: 뷰어가 draft 본문 파싱에서 추출한 body 스타일 골격 보고.
      // EmailForm initEditor 의 defaultStyle 시딩이 본문 전체 재파싱 대신 사용한다.
      reportDraftBodyStyle: (
        emailId: string,
        skeletonHTML: string,
        bodyLen: number,
        bodyPrefix: string,
      ): void => {
        setDraftBodyStyleSkeleton(emailId, { skeletonHTML, bodyLen, bodyPrefix })
      },
      // parsed-body-doc-from-viewer: 뷰어가 본문 표시 파싱 직후(mutate 전) 떠 둔 원본 보존
      // Document(clone) 수신 — same-realm 직접 호출 전제. 이 핸들러의 등록 여부가
      // **Gate A(전송 능력)** 다: 뷰어 ViewerIFace 가 핸들러 존재를 확인한 뒤에만 clone 을
      // 생성·전달하므로 등록 자체가 기능 토글. 캡처 대상 선별은 **Gate B(정책)** 인
      // EmailInfoData.enable_capture_element 가 담당한다(parsedBodyDocCache 모듈 주석 참조 —
      // 역할이 달라 한쪽을 중복으로 오인해 제거하면 안 된다). EmailForm 이 draft 편집·reply/
      // forward·resend 에서 소비해 본문 전체 재파싱(~4s)을 제거한다.
      ...(BODY_DOC_FROM_VIEWER
        ? {
            reportParsedBodyDocument: (
              emailId: string,
              doc: Document,
              sourceHTML: string,
            ): void => {
              // realm-aware 수용: iframe 모드(모바일 WebView / __forceIFrameViewer escape hatch)의
              // Document 는 다른 realm 소속이라 host `instanceof Document` 에 걸리지 않는다. 그러나
              // same-origin iframe 은 같은 프로세스/힙이라 live DOM 참조가 유효하므로 nodeType 으로
              // Document 를 판별해 수용한다. iframe 폐기 후 죽은 realm 노드/상이한 baseURI/instanceof
              // 정체성 문제는 수신측(setParsedBodyDocument)이 host realm 으로 importNode 하여 격리한다.
              if (doc == null || doc.nodeType !== 9 /* DOCUMENT_NODE */) return
              setParsedBodyDocument(emailId, doc, sourceHTML)
            },
          }
        : {}),
      editDraft: (emailId: string): void => {
        const { openSendMail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        const { subject } = email.data

        if (openSendMail != null) {
          openSendMail({
            cmd: 'draft',
            title: i18n.t('email.title.editDraft'),
            draftEmail: email,
            extValues: {
              subject,
            },
          })
        }
      },
      deleteDraft: (emailId: string): boolean => {
        const { deleteDraft } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        if (deleteDraft != null) {
          deleteDraft(email)
        }
        return false
      },
      openURL: (emailId: string, url: string): void => {
        const pos = url.indexOf('mailto:')
        if (pos === 0) {
          const email = url.substring(7)
          const { openSendMail } = reactObj.props
          openSendMail!({
            cmd: 'normal',
            extValues: { to: [email] },
          })
        } else {
          window.open(url, '_blank')!.focus()
        }
      },
      onClickPhoneNumber: (url): void => {
        window.open(url, '_blank')!.focus()
      },
      showContact: (email: string, name: string): void => {
        // const { openSendMail } = reactObj.props
        // openSendMail({
        //   cmd: 'normal',
        //   extValues: { to: [email] },
        // })
        modalContactDetail(name, email)
      },
      showFindDialog: (emailId: string): void => {
        notImplemented(`showFindDialog = ${emailId}`)
      },
      newMail: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        replyOrForwardEmail('replyAllNew', email)
      },
      sendAgain: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        replyOrForwardEmail('sendAgain', email)
      },
      reply: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        replyOrForwardEmail('reply', email)
      },
      replyAll: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        replyOrForwardEmail('replyAll', email)
      },
      forward: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const { email } = reactObj.emailInfoMap[emailId]
        replyOrForwardEmail('forward', email)
      },
      forwardAsAttachment: (emailId: string): void => {
        const { replyOrForwardEmail } = reactObj.props
        const emailInfo = reactObj.emailInfoMap[emailId]
        console.log(' ---- emailInfo : ', emailInfo)
        if (emailInfo != null) {
          const { email } = emailInfo
          replyOrForwardEmail('forwardAsAttachment', email)
        }
      },
      getAiSummary: (emailId: string, refresh: boolean, returnFunc: string): null => {
        setTimeout(() => {
          const retProp = {
            ai_summary:
              'This is AI summary...!!! This is AI summary...!!! This is AI summary...!!!This is AI summary...!!! This is AI summary...!!! This is AI summary...!!! This is AI summary...!!! This is AI summary...!!! ',
            ai_detail_summary:
              'This is AI summary...!!! detail~~!!!!!!! This is AI summary...!!! detail~~!!!!!!! This is AI summary...!!! detail~~!!!!!!! This is AI summary...!!! detail~~!!!!!!! This is AI summary...!!! detail~~!!!!!!! This is AI summary...!!! detail~~!!!!!!! ',
            ai_summary_feedback: null,
            // success: false,
          }
          console.log(' --- getAiSummary called.', emailId, refresh)
          const retStr = JSON.stringify(retProp)
          callCBStr(returnFunc, [retStr])
        }, 2000)

        return null
      },
      aiSummaryFeedback: (emailId: string, value: string): void => {
        console.log(' --- aiSummaryFeedback : ', emailId, value)
        const v = value != null ? value.toLowerCase() : null
        const changed = {
          ai_summary_feedback: v === 'up',
        }
        const changedStr = JSON.stringify(changed)
        viewerWindow.webObj.notifyEmailChanged(emailId, changedStr)
      },
      getAiExtraction: (emailId: string, refresh: boolean, returnFunc: string): null => {
        if (aiExtractionSample != null) {
          setTimeout(async () => {
            const sample = aiExtractionSample
            const retProp = {
              ai_extraction: sample,
              ai_extraction_feedback: null,
              // success: false,
            }
            console.log(' --- getAiSummary called.', emailId, refresh)
            const retStr = JSON.stringify(retProp)
            callCBStr(returnFunc, [retStr])
          }, 2000)
        }
        return null
      },
      aiExtractionFeedback: (emailId: string, value: string): void => {
        console.log(' --- aiSummaryFeedback : ', emailId, value)
        const v = value != null ? value.toLowerCase() : null
        const changed = {
          ai_extraction_feedback: v === 'up',
        }
        const changedStr = JSON.stringify(changed)
        viewerWindow.webObj.notifyEmailChanged(emailId, changedStr)
      },
      changeTaskStatus: (
        emailId: string,
        taskId: string,
        taskStatus: string,
        returnFunc: string,
      ): null => {
        if (aiExtractionSample != null) {
          setTimeout(async () => {
            const { tasks } = aiExtractionSample
            const task = tasks.find((item: any) => item.id === taskId)
            task.task_status = taskStatus

            console.log(' --- changeTaskStatus called.', emailId, taskId, taskStatus)
            const retStr = JSON.stringify(task)
            callCBStr(returnFunc, [retStr])

            const changed = {
              ai_extraction: {
                tasks,
              },
            }
            const changedStr = JSON.stringify(changed)
            // unmount 후 발화할 수 있는 타이머라 webObj 가드.
            const webObj = getViewerWebObj(viewerWindow)
            if (webObj != null) {
              webObj.notifyEmailChanged(emailId, changedStr)
            }
          }, 2000)
        }

        return null
      },
      changeEventStatus: (
        emailId: string,
        eventId: string,
        status: string,
        returnFunc: string,
      ): null => {
        if (aiExtractionSample != null) {
          setTimeout(async () => {
            const { events } = aiExtractionSample
            const event = events.find((item: any) => item.id === eventId)
            event.status = status

            const retStr = JSON.stringify(event)
            callCBStr(returnFunc, [retStr])

            const changed = {
              ai_extraction: {
                events,
              },
            }
            const changedStr = JSON.stringify(changed)
            // unmount 후 발화할 수 있는 타이머라 webObj 가드.
            const webObj = getViewerWebObj(viewerWindow)
            if (webObj != null) {
              webObj.notifyEmailChanged(emailId, changedStr)
            }
          }, 2000)
        }

        return null
      },
      clearTaskStatus: (emailId: string, taskId: string): null => {
        notImplemented(`clearTaskStatus - ${emailId}, ${taskId}`)
        return null
      },
      reservationMailReschedule: (emailId: string): null => {
        const emailInfo = reactObj.emailInfoMap[emailId]
        if (emailInfo != null) {
          const { email } = emailInfo

          const { runCommand } = reactObj.props
          runCommand('rescheduleEmail', [email])
        }
        return null
      },
      reservationMailCancelSend: (emailId: string): null => {
        const emailInfo = reactObj.emailInfoMap[emailId]
        if (emailInfo != null) {
          const { email } = emailInfo
          // console.log(' ---- reservationMailCancelSend : ', email)
          const { runCommand } = reactObj.props
          runCommand('cancelScheduledEmail', [email])
        }
        return null
      },
      reservationMailDelete: (emailId: string): null => {
        const { email } = reactObj.emailInfoMap[emailId]
        const { runCommand } = reactObj.props
        runCommand('delete', [email])
        return null
      },

      viewMessageDetails: (emailId: string): void => {
        console.log(`viewMessageDetails - ${emailId}`)
        const emailInfo = reactObj.emailInfoMap[emailId]
        console.log(' ---- emailInfo : ', emailInfo)
        if (emailInfo != null) {
          const { email } = emailInfo
          email
            .reqDetail()
            .then((detail: any) => {
              const { headers } = detail
              let lines = headers.map((header: any, idx: number) => {
                const { name, value } = header
                return (
                  <div key={idx}>
                    {name}: {value}
                  </div>
                )
              })

              popupDoc({
                wrapClassName: classNames('etv-container'),
                title: i18n.t('email.title.messageDetail'),
                content: (
                  <div className={classNames(styles.popupContent, 'popupContent')}>
                    <div className={classNames(styles.contentList)}>{lines}</div>
                  </div>
                ),
              })
            })
            .catch((err: any) => {
              console.error(' - viewMessageDetails reqDetail error - ', err)
            })
        }
      },
      responseMeeting: (emailId: string, type: number, responseMeeting: number): void => {
        // type
        // 0: accept
        // 1: tentative
        // 2: decline
        // 3: remove-calendar
        // 4: propose-review
        // 5: propose-accept
        //
        // responseMeeting
        // 8: 보내기 전에 응답 편집,
        // 16: 지금 응답 보내기,
        // 32: 답장 보지 않기,
        // 128: 새로운 시간 제안.
        // notImplemented(`responseMeeting - ${emailId}, ${type}, ${responseMeeting}`)
        const { runCommand } = reactObj.props
        const emailInfo = reactObj.emailInfoMap[emailId]
        const { meeting_info, email } = emailInfo
        if (meeting_info != null) {
          const callMeetingResponse = (typeNum: number, text: string | null): void => {
            console.log(' ---- meeting_info : ', meeting_info)
            const { uid, recurrenceId } = meeting_info
            console.log(' ---- uid, recurrenceId : ', uid, recurrenceId)
            const recurrentIdDate = recurrenceId != null ? new Date(recurrenceId) : null

            let typeStr: 'ACCEPTED' | 'TENTATIVE' | 'DECLINED' = 'DECLINED'
            if (typeNum === 0) {
              typeStr = 'ACCEPTED'
            } else if (typeNum === 1) {
              typeStr = 'TENTATIVE'
            } else if (typeNum === 2) {
              typeStr = 'DECLINED'
            }

            let resultObj = showResult({
              loading: true,
              closeOnClick: false,
              message: i18n.t('meeting.response.sending'),
              duration: 10, // second.
            })

            meetingResponseByUid(uid, recurrentIdDate, typeStr, text, (success: boolean) => {
              console.log(' --- responseMeeting success!', success)
              // curMailbox.deleteEmails([email])
              if (success === true) {
                // trash에 포함되지 않으면 삭제
                resultObj = showResult({
                  key: resultObj.key,
                  loading: false,
                  closeOnClick: true,
                  message: i18n.t('meeting.response.sent'),
                  duration: 1, // second.
                })

                if (dataManager.getTrash().isContain(email) !== true) {
                  runCommand('delete', [email])
                }
              } else {
                resultObj.close()
                showResult({
                  key: resultObj.key + 'failed',
                  loading: false,
                  closeOnClick: true,
                  message: i18n.t('meeting.response.failed'),
                  duration: 5, // second.
                  actionText: i18n.t('common.button.retry'),
                  action: () => {
                    callMeetingResponse(type, text)
                  },
                })
              }
            })
          }

          if (responseMeeting === 8) {
            // 보내기 전에 응답 편집
            openCommentDialog({
              type,
              title: i18n.t('meeting.resComment.edit'),
              placeholder: i18n.t('meeting.resComment.placeholder'),
              onOk: (typeNum: number, text: string) => {
                callMeetingResponse(typeNum, text)
              },
            })
            // meetingResponseByUid(uid, recurrentIdDate, typeStr, responseMeeting)
          } else if (responseMeeting === 16) {
            // 지금 응답 보내기
            callMeetingResponse(type, null)
          }
        }
      },
      saveEmail: (emailId: string): void => {
        console.log(`saveEmail - ${emailId}`)
        const emailInfo = reactObj.emailInfoMap[emailId]
        console.log(' ---- emailInfo : ', emailInfo)
        if (emailInfo != null) {
          const { email, detail } = emailInfo

          const resultObj = showResult({
            loading: true,
            closeOnClick: false,
            message: i18n.t('email.result.saving'),
          })

          // const attachments = [...(inlineFiles || []), ...body.attachments]
          const { attachments } = detail
          const attachmentList = attachments.map((file: any) => {
            // createAttachmentFromDraft(draftFile, reactObj?: ReactObj | null) — 두 번째 인자
            // 가 optional + nullable 이므로 cast 없이 null 직접 전달.
            return attachmentHelper.createAttachmentFromDraft(file, null)
          })

          email.saveEML(attachmentList).then(({ error }: any) => {
            if (error != null) {
              errorBase({
                title: null,
                content: 'save failed',
              })
            }
            resultObj.close()
          })
        }
      },
      setPriorityInbox: (emailId: string, priorityInbox: boolean): void => {
        console.log(' --- setPriorityInbox : ', emailId)
        const changed = {
          priority_inbox: priorityInbox,
        }
        const changedStr = JSON.stringify(changed)
        viewerWindow.webObj.notifyEmailChanged(emailId, changedStr)
      },
      setThreadImportant: (important: boolean, returnFunc: string): null => {
        if (threadInfo != null) {
          setTimeout(async () => {
            const info = threadInfo
            info.important = important
            callCBStr(returnFunc, [info.important])
          }, 2000)
        }

        return null
      },
      onThreadLabelClick: (id: string, name: string): void => {
        notImplemented(`onThreadLabelClick - ${id}, ${name}`)
      },
      showInlineImage: (url: string): void => {
        // emailID가 없으므로 전체에서 url을 찾는다.
        Object.keys(reactObj.emailInfoMap).some((emailId: string) => {
          const emailInfo = reactObj.emailInfoMap[emailId]
          const { inlineFilesMap } = emailInfo
          if (inlineFilesMap != null) {
            const inlineFile = inlineFilesMap[url]
            if (inlineFile != null) {
              inlineFile.showPreview()
              return true
            }
          }
          return false
        })
      },
      download: (emailId: string, attachmentId: string): Promise<void> | null => {
        const { attachmentMap } = reactObj.emailInfoMap[emailId]
        const attachmentObj = attachmentMap![attachmentId]

        const onProgress = (e: any, progress: number): void => {
          console.log('--- onProgress : ', e, progress)
          // 다운로드(HTTP 전송) 중 화면 이탈(unmount→about:blank) 시 늦게 발화할 수 있어 webObj 가드.
          const webObj = getViewerWebObj(viewerWindow)
          if (webObj != null) {
            webObj.notifyDownloadProgress(emailId, attachmentId, progress)
          }
        }

        if (attachmentObj != null) {
          return attachmentObj.download(onProgress).then((ret: any) => {
            if (ret != null) {
              ret.saveFile()
              // ret는 jmapDownloadCache가 공유 반환하는 객체이므로 여기서 revoke하지 않는다.
              // blob URL 수명은 캐시 TTL / clear / 덮어쓰기 시점에 일괄 해제된다.
            }
          })
        }

        return null
      },
      cancelDownload: (emailId: string, attachmentId: string): void => {
        const { attachmentMap } = reactObj.emailInfoMap[emailId]
        const attachmentObj = attachmentMap![attachmentId]

        if (attachmentObj != null) {
          attachmentObj.cancelProgress()
        }
      },
      downloadAll: async (emailId: string): Promise<void> => {
        const { attachmentMap, email } = reactObj.emailInfoMap[emailId]
        console.log(' ---- downloadAll : ', attachmentMap)

        const promiseList = Object.keys(attachmentMap!).map(async (key) => {
          return await attachmentMap![key].download()
        })

        const retList = await Promise.all(promiseList)

        const zip = new JSZip()

        const fileNameMap = new FileNameMgr()

        retList.forEach((ret: any) => {
          const name = fileNameMap.generateFilename(ret.file.name)
          zip.file(name, ret.blob)
        })

        const blob = await zip.generateAsync({ type: 'blob' })

        const downloadPath = window.URL.createObjectURL(blob)

        let fileName = email.data.subject
        if (fileName == null || fileName.length === 0) {
          fileName = i18n.t('editor.value.noSubject')
        }

        const link = document.createElement('a')
        link.href = downloadPath
        link.download = generateFilename(`${fileName}.zip`)
        link.click()
        link.remove() // IE 미지원
        // zip blob URL은 캐시 밖에서 이번 호출 전용으로 생성된 것이므로 click 직후 해제한다.
        // click이 blob을 읽기 시작한 뒤 실행되도록 마이크로 지연.
        setTimeout(() => safeRevokeObjectURL(downloadPath), 0)

        // retList의 개별 ret는 jmapDownloadCache가 공유하는 객체이므로 여기서 해제하지 않는다.
      },
      downloadFile: (emailId: string, attachmentId: string): void => {
        const { attachmentMap } = reactObj.emailInfoMap[emailId]
        const attachmentObj = attachmentMap![attachmentId]

        if (attachmentObj != null) {
          attachmentObj.download().then((ret: any) => {
            if (ret != null) {
              ret.saveFile()
              // ret는 jmapDownloadCache의 공유 객체. blob URL은 캐시가 관리한다.
            }
          })
        }
      },
      open: (emailId: string, attachmentId: string): void => {
        // notImplemented(`open - ${emailId}, ${attachmentId}`)
        const { attachmentMap } = reactObj.emailInfoMap[emailId]
        const attachmentObj = attachmentMap![attachmentId]

        if (attachmentObj != null) {
          const { runCommand, openSendMail, replyOrForwardEmail, deleteDraft } = reactObj.props
          const apiMap = {
            openSendMail,
            replyOrForwardEmail,
            deleteDraft,
            runCommand,
          }
          if (attachmentObj.showPreview(apiMap) !== true) {
            viewerWindow.NineNative.download(emailId, attachmentId)
          }
        }
      },
      saveAs: (emailId: string, attachmentId: string): void => {
        notImplemented(`saveAs - ${emailId}, ${attachmentId}`)
      },
      showPreview: (emailId: string, attachmentId: string): void => {
        const { attachmentMap } = reactObj.emailInfoMap[emailId]
        const attachmentObj = attachmentMap![attachmentId]

        if (attachmentObj != null) {
          const { runCommand, openSendMail, replyOrForwardEmail, deleteDraft } = reactObj.props
          const apiMap = {
            openSendMail,
            replyOrForwardEmail,
            deleteDraft,
            runCommand,
          }
          if (attachmentObj.showPreview(apiMap) !== true) {
            viewerWindow.NineNative.download(emailId, attachmentId)
          }
        }
      },
      saveAndOpen: (emailId: string, attachmentId: string): void => {
        notImplemented(`saveAndOpen - ${emailId}, ${attachmentId}`)
      },
      showInFolder: (emailId: string, attachmentId: string): void => {
        notImplemented(`showInFolder - ${emailId}, ${attachmentId}`)
      },
      createInvitation: (startDate: any, endDate: any, displayStr: string): void => {
        console.log(' --- sendInvitation : ', startDate, endDate, displayStr)
        reactObj.onCreateInvitation(startDate, endDate, displayStr)
      },
      secureSendRenewAccess: (emailId: string, returnFunc: string): null => {
        const emailInfo = reactObj.emailInfoMap[emailId]
        const secureMailId = emailInfo?.secureSendInfo?.secureMailId
        const expiredDate = emailInfo?.secureSendInfo?.expiredDate

        if (secureMailId == null) {
          console.error('secureSendRenewAccess: secureMailId not found for emailId:', emailId)
          showResult({ message: i18n.t('secureSend.errorNotFound') })
          callCBStr(returnFunc, [JSON.stringify(false)])
          return null
        }

        confirmBase({
          title: i18n.t('secureSend.renewAccess'),
          content: i18n.t('secureSend.confirmRenewAccess'),
          icon: (
            <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />
          ),
          okText: i18n.t('common.button.ok'),
          onOk: (closeFunc: () => void) => {
            closeFunc()
            secureSendApiHelper
              .renewSecureMailAccess(secureMailId)
              .then(() => {
                // 모달이 unmount 후까지 떠 있다가 OK될 수 있어 webObj 가드.
                const webObj = getViewerWebObj(viewerWindow)
                if (webObj != null) {
                  webObj.setSecureSendInfo(
                    emailId,
                    JSON.stringify({ isSecureMail: true, expiredDate, secureMailId }),
                  )
                }
                callCBStr(returnFunc, [JSON.stringify(true)])
              })
              .catch((err: any) => {
                console.error('secureSendRenewAccess error:', err)
                callCBStr(returnFunc, [JSON.stringify(false)])
              })
          },
        })

        return null
      },
      secureSendRemoveAccess: (emailId: string, returnFunc: string): null => {
        const emailInfo = reactObj.emailInfoMap[emailId]
        const secureMailId = emailInfo?.secureSendInfo?.secureMailId

        if (secureMailId != null) {
          confirmBase({
            title: i18n.t('secureSend.removeAccess'),
            content: i18n.t('secureSend.confirmRemoveAccess'),
            icon: (
              <img
                src={exclamationCircleRed22}
                style={{ paddingRight: '16px' }}
                alt="exclamation"
              />
            ),
            okText: i18n.t('common.button.ok'),
            onOk: (closeFunc: () => void) => {
              closeFunc()
              secureSendApiHelper
                .removeSecureMailAccess(secureMailId)
                .then(() => {
                  callCBStr(returnFunc, [JSON.stringify(true)])
                })
                .catch((err: any) => {
                  console.error('secureSendRemoveAccess error:', err)
                  callCBStr(returnFunc, [JSON.stringify(false)])
                })
            },
          })
        } else {
          callCBStr(returnFunc, [JSON.stringify(false)])
        }
        return null
      },
      // Phase 7 (5차 보완) — viewer (칩 클릭) → host: status lookup 후 host 컨텍스트에서 popup 마운트
      // (iframe 마스크 클립 회피)
      // 5차 보완 후속: 팝업이 emailId 를 구독하여 refresh 결과를 자동 반영하도록
      // initialStatus 와 emailId 를 함께 전달한다 (readReceiptListenerStore).
      openReadReceiptDetailPopup: (emailId: string): null => {
        const initialStatus = reactObj.emailInfoMap[emailId]?.readReceiptInfo?.status
        if (initialStatus != null) {
          openReadReceiptDetailPopup({ emailId, initialStatus })
        }
        return null
      },
      // Phase 2.7 — viewer (refresh 클릭) → host: 캐시 삭제 후 applyReadReceiptInfo 재호출
      requestRefreshReadReceiptInfo: async (emailId: string): Promise<null> => {
        const emailInfo = reactObj.emailInfoMap[emailId]
        const receiptId = emailInfo?.readReceiptInfo?.receiptId
        if (receiptId != null && receiptId.length > 0) {
          try {
            await readReceiptStatusCacheDB.deleteData(receiptId)
          } catch (err) {
            console.error('requestRefreshReadReceiptInfo - deleteData error:', emailId, err)
          }
        }
        applyReadReceiptInfo(viewerWindow, emailId, reactObj, true)
        return null
      },
    } satisfies NineNativeApi
  }

  // disposeViewerApi에서 소유 여부를 판별할 수 있도록 등록한 함수를 기록.
  // 모듈 변수(threadViewInfo)와 인스턴스(reactObj.ownInitNative) 양쪽에 기록 —
  // 인스턴스 토큰은 다중 마운트 경쟁에서 자기 등록만 정확히 해제하기 위함.
  // (InnerViewer도 reactObj.ownInitNative를 갖지만 disposeViewerApi를 호출하지 않으므로 무해.)
  threadViewInfo.initNative = window.initNative
  reactObj.ownInitNative = window.initNative
}

function openCommentDialog(options: CommentDialogOptions): any {
  const tmpObj: any = {
    title: options.title,
    type: options.type,
    placeholder: options.placeholder,
    text: '',
  }

  tmpObj.onTextChange = (e: any) => {
    // console.log(' --- onTextChange : ', e.target.value)
    tmpObj.text = e.target.value
  }

  tmpObj.getTitle = () => {
    return tmpObj.title
  }

  tmpObj.render = () => {
    return (
      <div className={classNames(styles.comment)}>
        <OmInput
          multiline
          onChangeEvent={tmpObj.onTextChange}
          rows={5}
          placeholder={tmpObj.placeholder}
        />
      </div>
    )
  }

  tmpObj.modalObj = confirmBase({
    // wrapClassName: classNames('noPadding', 'noBtn', styles.settingsModal),
    wrapClassName: classNames(styles.calendarComment),
    title: tmpObj.getTitle(),
    content: tmpObj.render(),
    closable: true,
    footer: (_: any, { OkBtn }: any) => {
      const selectAttrs = {
        className: classNames(styles.typeSelect),
        popupClassName: classNames('contextMenu'),
        suffixIcon: <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />,
        defaultValue: tmpObj.type as number,
        options: [
          { label: i18n.t('common.button.accept'), value: 0 },
          { label: i18n.t('common.button.tentative'), value: 1 },
          { label: i18n.t('common.button.decline'), value: 2 },
        ],
        onChange: (value: number) => {
          tmpObj.type = value
        },
      }
      return (
        <div className={classNames(styles.footer)}>
          <div className={classNames('btns')}>
            <OmSelect {...selectAttrs} />
            <OkBtn />
          </div>
        </div>
      )
    },
    width: 480,
    // footer: null,
    okText: i18n.t('common.button.send'),
    // okButtonProps: { disabled: tmpObj.saveDisabled },
    onOk: () => {
      console.log(' --- onOk : ', tmpObj.type, tmpObj.text)
      if (options.onOk != null) {
        options.onOk(tmpObj.type, tmpObj.text)
      }
    },
    afterClose: () => {
      if (options.afterClose != null) {
        options.afterClose()
      }
    },
  })

  tmpObj.updateModal = () => {
    tmpObj.modalObj.updateBase({
      title: tmpObj.getTitle(),
      content: tmpObj.render(),
      // okButtonProps: { disabled: tmpObj.saveDisabled },
    })
  }

  return tmpObj
}

export function getThreadViewInfo(): ThreadViewInfo {
  return threadViewInfo
}

// Secure Send: 이메일의 header를 분석하여 보안 메일 여부를 판별하고 setSecureSendInfo를 호출한다.
// getBody에서 callCBStr 직전에 호출된다 (이메일 상세 정보가 로드된 시점).
function applySecureSendInfo(viewerWindow: ViewerWindow, emailId: string, reactObj: any): void {
  const email = dataManager.getEmailById(emailId)
  if (email == null) return

  email
    .reqDetail()
    .then(async (detail: any) => {
      if (detail == null || detail.headers == null) return

      const secureMailHeader = detail.headers.find(
        (h: any) => h.name.toLowerCase() === 'x-secure-mail',
      )
      const secureMailIdHeader = detail.headers.find(
        (h: any) => h.name.toLowerCase() === 'x-secure-mail-id',
      )

      const userEmail = getEnvParam('userEmail')?.toLowerCase()
      if (userEmail == null || userEmail.length === 0) return

      // 3차 보완: from 매칭 OR 보낸편지함 위치
      const isSentByMe = isMailSentByMe(email, detail, userEmail)

      const isSecureMail = secureMailHeader?.value?.trim() === 'true'
      const secureMailId = secureMailIdHeader?.value?.trim()

      if (isSentByMe && isSecureMail && secureMailId != null && secureMailId.length > 0) {
        // 캐시 lookup — BaseDB record는 { secure_mail_id, data: { expired_date }, expireTime }
        const cached = await secureMailStatusCacheDB.getData(secureMailId)
        if (cached?.data?.expired_date != null) {
          const expired = new Date(cached.data.expired_date) < new Date()
          if (expired) {
            // 만료 — viewer에 아무것도 전달하지 않고 종료 (일반 메일처럼 표시)
            return
          }
        }

        secureSendApiHelper
          .getSecureMailStatus(secureMailId)
          .then(async (statusData: SecureMailStatusResponse) => {
            const secureSendInfo = {
              isSecureMail: statusData?.is_secure_mail === true,
              expiredDate: statusData?.expired_date ?? null,
              secureMailId,
            }

            if (reactObj.emailInfoMap[emailId] != null) {
              reactObj.emailInfoMap[emailId].secureSendInfo = secureSendInfo
            }

            // async(.then) 경로 — teardown 후 발화 가능하므로 guard 접근.
            getViewerWebObj(viewerWindow)?.setSecureSendInfo(
              emailId,
              JSON.stringify(secureSendInfo),
            )

            // expired_date 캐시 저장 (BaseDB가 자체 wrap 처리)
            await secureMailStatusCacheDB.addData(secureMailId, {
              expired_date: statusData?.expired_date ?? null,
            })
          })
          .catch((err: any) => {
            console.error('applySecureSendInfo - getSecureMailStatus error:', emailId, err)
          })
      }
    })
    .catch((err: any) => {
      console.error('applySecureSendInfo - reqDetail error:', emailId, err)
    })
}

// ===== Read Receipt =====

function dispatchReadReceiptInfoToViewer(
  viewerWindow: ViewerWindow,
  emailId: string,
  reactObj: any,
  isReadReceipt: boolean,
  receiptId: string,
  statusData: SendStatusResponse | null,
): void {
  const info = statusData != null ? { receiptId, status: statusData } : null
  if (reactObj.emailInfoMap[emailId] != null) {
    reactObj.emailInfoMap[emailId].readReceiptInfo = info
  }
  // async(.then) 경로라 teardown(about:blank) 후 발화 가능 — 직접 접근 대신 guard.
  const webObj = getViewerWebObj(viewerWindow)
  webObj?.setReadReceiptInfo(emailId, isReadReceipt, info != null ? JSON.stringify(info) : null)
  // Read‑Receipt 상세 팝업이 열려 있으면 동일 emailId 의 listener 에게 갱신 통지.
  // statusData=null 은 "갱신 시작 — 로딩" 신호로 그대로 전파한다.
  notifyReadReceipt(emailId, statusData)
}

function applyReadReceiptInfo(
  viewerWindow: ViewerWindow,
  emailId: string,
  reactObj: any,
  isRefresh = false,
): void {
  const email = dataManager.getEmailById(emailId)
  if (email == null) return

  email
    .reqDetail()
    .then(async (detail: any) => {
      if (detail == null || detail.headers == null) return

      // 헤더 기반 판정 (CLIENT_API_GUIDE.md v2026.04.28-5):
      // - X-Officemail-Send-ID 가 있으면 보낸편지함에 있는 본인 발송 메일 → isSentByMe
      // - X-Tracking-ID 만 있으면 받은 메일 → isReceivedByMe
      // (둘 중 있는 값이 receiptId — send_id 또는 receipt_id)
      //
      // ⚠ applySecureSendInfo의 isMailSentByMe(from 매칭 OR 보낸편지함 위치)와의 비대칭은
      //   의도된 설계 — 4차 보완(openspec/changes/feature-read-receipt/design.md L69, L865).
      //   X-Officemail-Send-ID는 IMAP APPEND 시 보낸편지함에만 자동 주입되므로 헤더 존재만으로
      //   본인 발송 판정이 충분하다. (※ 비대칭 자체는 review에서 재지적되지 않도록 명기)
      const trackingHeader = detail.headers.find(
        (h: any) => h.name.toLowerCase() === 'x-tracking-id',
      )
      const sendIdHeader = detail.headers.find(
        (h: any) => h.name.toLowerCase() === 'x-officemail-send-id',
      )
      const sendIdValue = sendIdHeader?.value?.trim()
      const trackingIdValue = trackingHeader?.value?.trim()
      const receiptId =
        sendIdValue != null && sendIdValue.length > 0 ? sendIdValue : trackingIdValue
      if (receiptId == null || receiptId.length === 0) return

      // 90일 만료 가드 — JMAP 표준 receivedAt 사용 (sentAt 폴백)
      const sentAtStr = detail.receivedAt ?? detail.sentAt
      if (sentAtStr != null) {
        const sentAt = new Date(sentAtStr).getTime()
        if (Number.isFinite(sentAt) && Date.now() - sentAt > READ_RECEIPT_RETENTION_MS) {
          return
        }
      }

      const isSentByMe = sendIdValue != null && sendIdValue.length > 0
      const isReceivedByMe = !isSentByMe && trackingIdValue != null && trackingIdValue.length > 0

      if (isSentByMe) {
        // 발송자 본인 분기: 칩/팝업 UI를 위한 status 조회 + 캐시
        const cached = await readReceiptStatusCacheDB.getData(receiptId)
        if (cached != null && cached.data != null) {
          dispatchReadReceiptInfoToViewer(
            viewerWindow,
            emailId,
            reactObj,
            true,
            receiptId,
            cached.data,
          )
        } else {
          // detail.messageId 는 string 또는 string[] 가능 — JMAP 표준 messageId 필드는 array.
          // getMessageId 가 < > 자동 부착, getStatusByMessageId 내부에서 encodeURIComponent 적용.
          const rawMessageId = Array.isArray(detail.messageId)
            ? detail.messageId[0]
            : detail.messageId
          if (
            rawMessageId == null ||
            typeof rawMessageId !== 'string' ||
            rawMessageId.length === 0
          ) {
            console.error('applyReadReceiptInfo: missing messageId for emailId:', emailId)
            return
          }
          // API 요청 직전 로딩 상태 디스패치 (statusData=null) — 칩 자리에 로딩 표시 노출
          // refresh 시에는 이미 isReadReceipt=true 가 확인된 상태이므로 칩을 그대로 유지하기 위해 로딩 dispatch 생략
          if (!isRefresh) {
            dispatchReadReceiptInfoToViewer(viewerWindow, emailId, reactObj, true, receiptId, null)
          }
          try {
            const statusData = await readReceiptApiHelper.getStatusByMessageId(
              getMessageId(rawMessageId),
            )
            dispatchReadReceiptInfoToViewer(
              viewerWindow,
              emailId,
              reactObj,
              true,
              receiptId,
              statusData,
            )

            if (statusData.read === statusData.total && statusData.total > 0) {
              await readReceiptStatusCacheDB.addData(receiptId, statusData)
            }
          } catch (err) {
            console.error('applyReadReceiptInfo - getStatusByMessageId error:', emailId, err)
            // 최초 진입에서 API 실패 시에만 isReadReceipt=false 로 칩/로딩 모두 숨김
            // refresh 시에는 기존 칩을 유지하기 위해 isReadReceipt=false dispatch 생략
            if (!isRefresh) {
              dispatchReadReceiptInfoToViewer(
                viewerWindow,
                emailId,
                reactObj,
                false,
                receiptId,
                null,
              )
            }
          }
        }
      }

      if (isReceivedByMe) {
        await maybeReportRead(receiptId)
      }
    })
    .catch((err: any) => {
      console.error('applyReadReceiptInfo - reqDetail error:', emailId, err)
    })
}

const ext = {
  initViewerApi,
}

export default ext
